import { useState, useEffect, useMemo, useCallback } from "react";
import { dataProvider } from "data";
import moment from "moment";
import {
  isInternationalTimezone,
  setNextStepsInLocalStorage,
} from "utils/AuthUtil";
import api from "data/APIs";
import { DEFAULT_TIME_FORMAT_12 } from "constants/dateTime";
import { getWhatsNextInfo } from "features/Onboarding/modules/WhatsNext/utils/whatsNext.utils";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { SOMETHING_WENT_WRONG } from "constants/displayStrings";
import {
  OFFER_TARGET_TYPE,
  OFFER_TYPE,
  LTO_FORM_KEYS,
  TIMER_TYPE,
  DURATION_UNIT_MULTIPLIERS,
  LTO_EDIT_SESSION_KEY,
  ONE_HOUR_IN_SECONDS,
} from "./constants";
import { OFFERING_STATUSES } from "constants/schedule";
import constants from "constants/constants";

const useLimitedTimeOffer = ({
  allDynamicPagesData = [],
  infiniteListingOptions = [],
} = {}) => {
  const { notify } = useNotifications();

  // Utility function to determine when to show offering selector
  const shouldShowOfferingSelector = (targetType) => {
    return targetType === OFFER_TARGET_TYPE.SPECIFIC_OFFERING;
  };

  // Utility function to determine when to show dynamic link (payment page) selector
  const shouldShowDynamicLinkSelector = (targetType) => {
    return targetType === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE;
  };

  // Utility function to determine when to show price fields
  const shouldShowPriceFields = (values) => {
    if (values.target_type === OFFER_TARGET_TYPE.ALL_OFFERINGS) {
      return true;
    }
    return !!(
      values?.[LTO_FORM_KEYS.LISTINGS]?.length ||
      (values.target_type === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE &&
        values?.[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS]?.length)
    );
  };

  // Utility function to determine when to show international pricing
  const shouldShowInternationalPricing = (values) => {
    if (isEdit) return false;
    if (values.target_type === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE) {
      return false;
    }
    if (values.target_type === OFFER_TARGET_TYPE.ALL_OFFERINGS) {
      return true;
    }
    return (
      values.target_type === OFFER_TARGET_TYPE.SPECIFIC_OFFERING &&
      values?.[LTO_FORM_KEYS.LISTINGS]?.length > 0
    );
  };

  const [initialValues, setInitialValues] = useState(() => {
    const editRecordData = window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY)
      ? JSON.parse(window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY))
      : null;
    return {
      target_type:
        editRecordData?.target_type || OFFER_TARGET_TYPE.SPECIFIC_OFFERING,
      [LTO_FORM_KEYS.LISTINGS]: [],
      [LTO_FORM_KEYS.OFFER_TYPE]: OFFER_TYPE.AMOUNT,
      [LTO_FORM_KEYS.TIMER_TYPE]: TIMER_TYPE.FIXED,
      [LTO_FORM_KEYS.EVERGREEN_DURATION_VALUE]: 24,
      [LTO_FORM_KEYS.EVERGREEN_DURATION_UNIT]: "hours",
      offerName: editRecordData?.title || "Flash Deal",
      INR: true,
      [LTO_FORM_KEYS.INR_PRICE]: editRecordData?.price || null,
      [LTO_FORM_KEYS.PERCENTAGE_VALUE]: null,
      [LTO_FORM_KEYS.USD_CHECKBOX]: !!editRecordData?.price_international,
      [LTO_FORM_KEYS.USD_PRICE]: editRecordData?.price_international || null,
      start_date: moment(new Date()).format("YYYY-MM-DD"),
      end_date: moment(new Date()).add("days", 7).format("YYYY-MM-DD"),
      start_date_international: moment(new Date()).format("YYYY-MM-DD"),
      end_date_international: moment(new Date())
        .add("days", 7)
        .format("YYYY-MM-DD"),
      start_time: moment().add(1, "minutes").format(DEFAULT_TIME_FORMAT_12),
      end_time: "11:59 pm",
      start_time_international: "09:00 am",
      end_time_international: "11:00 pm",
      region: editRecordData?.region ?? (isInternationalTimezone() ? 1 : 0),
      [LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS]: [],
    };
  });
  const [isEdit, setIsEdit] = useState(false);
  const [firstLimitedOffer, setFirstLimitedOffer] = useState(false);
  const [limitedOfferCreative, setLimitedOfferCreative] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [listingUuid, setListingUuid] = useState("");
  const [offers, setOffers] = useState(0);
  const [selectedListing, setSelectedListing] = useState([]);

  const recordData = window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY)
    ? JSON.parse(window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY))
    : null;

  useEffect(() => {
    (async () => {
      try {
        const offerData = await dataProvider.custom_request(
          "host/offer/list",
          "GET"
        );
        if (offerData.status === 200) {
          setOffers(offerData.data.offers.length);
        }
      } catch (err) {
        notify(
          err?.body?.message ?? SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
      }
    })();

    if (window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY)) {
      setIsEdit(true);
      const recordData = window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY)
        ? JSON.parse(window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY))
        : null;
      const start_dateTime = DateCoversion(recordData.start_datetime);
      const end_dateTime = DateCoversion(recordData.end_datetime);
      const recordListingUids = Array.isArray(recordData.listing_uids)
        ? recordData.listing_uids.map((l) =>
            typeof l === "string" ? l : l.uuid
          )
        : recordData.listing_uid
        ? [recordData.listing_uid]
        : [];

      const normalizeComboPages = (pages) => {
        if (!pages) return [];
        return pages.map((cp) =>
          typeof cp === "string"
            ? { dynamic_link_id: cp, uuid: cp, title: cp }
            : cp
        );
      };
      const normalizedComboPages = normalizeComboPages(recordData.combo_pages);

      const normalizeTimerType = (val) => {
        if (val === TIMER_TYPE.EVERGREEN) return TIMER_TYPE.EVERGREEN;
        return TIMER_TYPE.FIXED;
      };
      const normalizeOfferType = (val) => {
        if (val === OFFER_TYPE.PERCENTAGE) return OFFER_TYPE.PERCENTAGE;
        return OFFER_TYPE.AMOUNT;
      };
      const metadataDurationSeconds =
        recordData?.metadata?.evergreen_duration_seconds;
      const metadataDurationUnit =
        recordData?.metadata?.evergreen_duration_unit || "hours";
      const displayValue = metadataDurationSeconds
        ? metadataDurationSeconds /
          (ONE_HOUR_IN_SECONDS *
            (DURATION_UNIT_MULTIPLIERS[metadataDurationUnit] || 1))
        : initialValues?.[LTO_FORM_KEYS.EVERGREEN_DURATION_VALUE];
      const metadataOfferType = normalizeOfferType(
        recordData?.metadata?.offer_type
      );
      const isPercentageOffer = metadataOfferType === OFFER_TYPE.PERCENTAGE;
      setInitialValues({
        ...initialValues,
        target_type: recordData?.target_type || initialValues?.target_type,
        [LTO_FORM_KEYS.OFFER_TYPE]: isPercentageOffer
          ? OFFER_TYPE.PERCENTAGE
          : OFFER_TYPE.AMOUNT,
        [LTO_FORM_KEYS.TIMER_TYPE]:
          normalizeTimerType(recordData?.metadata?.timer_type) ||
          initialValues?.[LTO_FORM_KEYS.TIMER_TYPE],
        [LTO_FORM_KEYS.EVERGREEN_DURATION_VALUE]: displayValue,
        [LTO_FORM_KEYS.EVERGREEN_DURATION_UNIT]: metadataDurationUnit,
        [LTO_FORM_KEYS.INR_PRICE]: isPercentageOffer ? null : recordData.price,
        [LTO_FORM_KEYS.USD_CHECKBOX]:
          !isPercentageOffer && !!recordData.price_international,
        [LTO_FORM_KEYS.USD_PRICE]:
          !isPercentageOffer && recordData.price_international
            ? parseFloat(recordData.price_international).toFixed(2)
            : null,
        [LTO_FORM_KEYS.PERCENTAGE_VALUE]: isPercentageOffer
          ? recordData?.metadata?.discount_percentage
          : null,
        [LTO_FORM_KEYS.LISTINGS]: recordListingUids,
        offerName: recordData.title,
        start_date: start_dateTime.date,
        start_time: start_dateTime.time,
        end_date: end_dateTime.date,
        end_time: end_dateTime.time,
        region: recordData.region,
        ...(recordData.target_type ===
          OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE && {
          combo_pages: normalizedComboPages,
          [LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS]: Array.isArray(
            recordData.dynamic_link_uuids
          )
            ? recordData.dynamic_link_uuids.map((d) =>
                typeof d === "string" ? d : d.dynamic_link_id
              )
            : normalizedComboPages
                .map((cp) => cp.dynamic_link_id)
                .filter(Boolean),
        }),
      });
    }
  }, []);

  const DateField = (dateConvert, onlyTime, payloadDate) => {
    const replaceUTC = onlyTime
      ? dateConvert
      : dateConvert?.replace("T", " ")?.replace("Z", "");
    let month;
    let day;
    let year;
    let time;
    if (!onlyTime) {
      const dateFormat = replaceUTC?.split(" ");
      const timeFormat = dateFormat && dateFormat[1]?.split(":");
      const date = new Date(dateFormat[0]);
      month = date.toLocaleString("default", { month: "short" });
      day = date.getDate();
      year = `${date.getFullYear()}`;

      let hours = timeFormat[0]; // gives the value in 24 hours format
      const AmOrPm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      let minutes = timeFormat[1];
      time =
        (hours < 10 ? `0${hours}` : hours) +
        ":" +
        (minutes < 10 ? `${minutes}` : minutes) +
        " " +
        AmOrPm;
    } else {
      const date = new Date(replaceUTC);
      month = payloadDate
        ? `${date.getMonth() < 9 ? 0 : ""}${date.getMonth() + 1}`
        : date.getMonth();
      day = date.getDate();
      year = date.getFullYear();
      let hours = date.getHours(); // gives the value in 24 hours format
      const AmOrPm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      let minutes = date.getMinutes();
      time =
        (hours < 10 ? `0${hours}` : hours) +
        ":" +
        (minutes < 10 ? `0${minutes}` : minutes) +
        " " +
        AmOrPm;
    }

    let date1 = `${year}-${month}-${day}`;
    return {
      date1,
      time,
    };
  };

  const DateCoversion = (entereddate) => {
    const replaceUTC = entereddate?.replace("T", " ")?.replace("Z", "");
    const dateFormat = replaceUTC?.split(" ");
    const timeFormat = dateFormat && dateFormat[1]?.split(":");
    const date = new Date(dateFormat[0]);
    let hours = timeFormat[0]; // gives the value in 24 hours format
    const AmOrPm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    let minutes = timeFormat[1];
    let time =
      (hours < 10 ? `0${hours}` : hours) +
      ":" +
      (minutes < 10 ? `${minutes}` : minutes) +
      " " +
      AmOrPm;

    return {
      time,
      date,
    };
  };

  const compareDates = (date, todaysDate) => {
    if (typeof todaysDate === "string" && todaysDate?.includes("-"))
      todaysDate = new Date(todaysDate);
    if (
      date?.getDate() === todaysDate?.getDate() &&
      date?.getFullYear() === todaysDate?.getFullYear() &&
      date?.getMonth() === todaysDate?.getMonth()
    ) {
      return true;
    }

    return false;
  };

  const handleCreateOffer = async (formState) => {
    const isPercentage =
      formState[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.PERCENTAGE;

    const rawTimerType = formState[LTO_FORM_KEYS.TIMER_TYPE];
    const isEvergreen = rawTimerType === TIMER_TYPE.EVERGREEN;
    const timerType = isEvergreen ? TIMER_TYPE.EVERGREEN : TIMER_TYPE.FIXED;

    let payload = {
      title: formState.offerName,
      target_type: formState.target_type,
      offer_type: formState[LTO_FORM_KEYS.OFFER_TYPE],
      timer_type: timerType,
    };

    if (isEvergreen) {
      payload.evergreen_duration_value =
        formState[LTO_FORM_KEYS.EVERGREEN_DURATION_VALUE];
      payload.evergreen_duration_unit =
        formState[LTO_FORM_KEYS.EVERGREEN_DURATION_UNIT];
    } else {
      payload.start_date = formState.start_date;
      payload.start_time = formState.start_time;
      payload.end_date = formState.end_date;
      payload.end_time = formState.end_time;
    }

    if (isPercentage) {
      payload.discount_percentage =
        formState[LTO_FORM_KEYS.PERCENTAGE_VALUE] &&
        parseFloat(formState[LTO_FORM_KEYS.PERCENTAGE_VALUE]);
    } else {
      payload.price_inr =
        formState[LTO_FORM_KEYS.INR_PRICE] &&
        parseFloat(formState[LTO_FORM_KEYS.INR_PRICE]).toFixed(2);
    }

    if (formState.target_type === OFFER_TARGET_TYPE.SPECIFIC_OFFERING) {
      payload.listing_uids = formState[LTO_FORM_KEYS.LISTINGS] || [];
    } else if (
      formState.target_type === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE
    ) {
      payload.dynamic_link_uuids =
        formState[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS] || [];
    }

    if (!isPercentage && formState[LTO_FORM_KEYS.USD_CHECKBOX]) {
      payload = {
        ...payload,
        price_international:
          formState[LTO_FORM_KEYS.USD_PRICE] &&
          parseFloat(formState[LTO_FORM_KEYS.USD_PRICE]).toFixed(2),
      };
      if (!isEvergreen) {
        payload.start_date_international = formState.start_date_international;
        payload.start_time_international = formState.start_time_international;
        payload.end_date_international = formState.end_date_international;
        payload.end_time_international = formState.end_time_international;
      }
    }

    if (window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY)) {
      const recordData = window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY)
        ? JSON.parse(window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY))
        : null;
      const isPercentageEdit =
        formState[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.PERCENTAGE;
      const editRawTimerType = formState[LTO_FORM_KEYS.TIMER_TYPE];
      const isEvergreenEdit = editRawTimerType === TIMER_TYPE.EVERGREEN;
      const editTimerType = isEvergreenEdit
        ? TIMER_TYPE.EVERGREEN
        : TIMER_TYPE.FIXED;

      const editPayload = {
        offer_uid: recordData.uuid,
        updated_values: {
          title: formState.offerName,
          target_type: formState.target_type,
          offer_type: formState[LTO_FORM_KEYS.OFFER_TYPE],
          timer_type: editTimerType,
        },
      };

      if (isEvergreenEdit) {
        editPayload.updated_values.evergreen_duration_value =
          formState[LTO_FORM_KEYS.EVERGREEN_DURATION_VALUE];
        editPayload.updated_values.evergreen_duration_unit =
          formState[LTO_FORM_KEYS.EVERGREEN_DURATION_UNIT];
      } else {
        if (
          !compareDates(
            DateCoversion(recordData.end_datetime).date,
            formState.end_date
          ) ||
          DateCoversion(recordData.end_datetime).time !== formState.end_time
        ) {
          editPayload.updated_values.end_date =
            DateField(recordData.end_datetime).time !== formState.end_time
              ? DateField(formState.end_date, true, true).date1
              : formState.end_date;
          editPayload.updated_values.end_time = formState.end_time;
        }
        if (
          !compareDates(
            DateCoversion(recordData.start_datetime).date,
            formState.start_date
          ) ||
          DateCoversion(recordData.start_datetime).time !== formState.start_time
        ) {
          editPayload.updated_values.start_date =
            DateField(recordData.start_datetime).time !== formState.start_time
              ? DateField(formState.start_date, true, true).date1
              : formState.start_date;
          editPayload.updated_values.start_time = formState.start_time;
        }
      }

      if (isPercentageEdit) {
        editPayload.updated_values.discount_percentage =
          formState[LTO_FORM_KEYS.PERCENTAGE_VALUE] &&
          parseFloat(formState[LTO_FORM_KEYS.PERCENTAGE_VALUE]);
      } else {
        const priceValue =
          formState[LTO_FORM_KEYS.INR_PRICE] &&
          parseFloat(formState[LTO_FORM_KEYS.INR_PRICE]).toFixed(2);
        if (recordData?.region === constants.domestic_region) {
          editPayload.updated_values.price_international = priceValue;
        } else {
          editPayload.updated_values.price_inr = priceValue;
        }
      }

      if (formState.target_type === OFFER_TARGET_TYPE.SPECIFIC_OFFERING) {
        editPayload.updated_values.listing_uids =
          formState[LTO_FORM_KEYS.LISTINGS] || [];
      } else if (
        formState.target_type === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE
      ) {
        editPayload.dynamic_link_uuids =
          formState[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS] || [];
      }

      if (!isPercentageEdit && formState[LTO_FORM_KEYS.USD_CHECKBOX]) {
        editPayload.updated_values.price_international =
          formState[LTO_FORM_KEYS.USD_PRICE] &&
          parseFloat(formState[LTO_FORM_KEYS.USD_PRICE]).toFixed(2);
      }
      try {
        const data = await dataProvider.custom_request(
          "host/offer/update",
          "POST",
          editPayload
        );
        if (data.status === 200) {
          window.sessionStorage.removeItem(LTO_EDIT_SESSION_KEY);
          notify(
            "Offer updated successfully!",
            notification_color_keys.success
          );
          return {
            listingOfferData: { updated: true },
            limitedOfferCreative: false,
          };
        }
      } catch (err) {
        notify(
          err?.body?.message ?? SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
      }
    } else {
      try {
        const data = await dataProvider.custom_request(
          "host/offer/create",
          "POST",
          payload
        );
        if (data.status === 200) {
          const domesticOffer = data?.data?.domestic_offer || {};
          const internationalOffer = data?.data?.international_offer || {};
          const isSingleTarget = domesticOffer.is_single_target !== false;
          const shareUrl =
            domesticOffer.share_url || internationalOffer.share_url;
          const shouldShowModal = isSingleTarget && !!shareUrl;
          let showCongrats = false;

          if (shareUrl) {
            setShareUrl(shareUrl);
          }

          if (shouldShowModal) {
            const offerData = await dataProvider.custom_request(
              api.get_offers,
              "GET"
            );
            const currentListing = offerData.data.offers.filter(
              (offer) =>
                (payload.listing_uids?.includes(offer.listing_uuid) &&
                  offer.status != OFFERING_STATUSES.soldout.value) ||
                offer?.uuid === domesticOffer?.uuid
            );
            setListingUuid(payload?.listing_uids?.[0] || payload?.uuid);
            setLimitedOfferCreative(true);
            setSelectedListing(currentListing?.[0]);
            showCongrats = true;
          }

          notify("Offer Created Successfully", notification_color_keys.success);
          const nextSteps = getWhatsNextInfo();
          if (nextSteps) {
            nextSteps.has_discount_code = true;
            setNextStepsInLocalStorage({ data: nextSteps });
          }
          if (offers === 0) {
            setFirstLimitedOffer(true);
            return { firstLimitedOffer: true };
          }

          return {
            listingOfferData: {
              region: initialValues.region,
              listing_uuid: payload.listing_uids?.[0],
              offer_type: payload.offer_type,
              share_url: shareUrl,
              ...(isPercentage
                ? { discount_percentage: payload.discount_percentage }
                : { price_inr: payload.price_inr }),
              ...(payload.price_international
                ? internationalOffer
                : domesticOffer),
            },
            limitedOfferCreative: showCongrats,
          };
        }
      } catch (err) {
        notify(
          err?.body?.message ?? SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
      }
    }
  };

  const listingTitleMap = useMemo(() => {
    if (!Array.isArray(recordData?.listing_uids)) return {};
    const map = {};
    recordData.listing_uids.forEach((l) => {
      const uuid = typeof l === "string" ? l : l.uuid;
      if (uuid) map[uuid] = typeof l === "string" ? l : l.title || uuid;
    });
    return map;
  }, [recordData?.listing_uids]);

  const getEditOfferingFallback = useCallback(
    (infiniteOptions) => {
      if (!isEdit || !initialValues[LTO_FORM_KEYS.LISTINGS]?.length)
        return infiniteOptions;
      const selectedUuids = initialValues[LTO_FORM_KEYS.LISTINGS];
      const merged = [...(infiniteOptions || [])];
      selectedUuids.forEach((uuid) => {
        if (!merged.find((o) => o.uuid === uuid || o.value === uuid)) {
          merged.push({
            uuid,
            id: uuid,
            value: uuid,
            label: listingTitleMap[uuid] || uuid,
          });
        }
      });
      return merged;
    },
    [isEdit, initialValues, listingTitleMap]
  );

  const comboPagesOptions = useMemo(() => {
    const currComboPages = initialValues.combo_pages;
    if (isEdit && currComboPages && currComboPages.length > 0) {
      return currComboPages.map((comboPage) => ({
        ...comboPage,
        id: comboPage.dynamic_link_id || comboPage.uuid,
        label: comboPage.title,
        value: comboPage.dynamic_link_id || comboPage.uuid,
      }));
    }
    return [];
  }, [initialValues.combo_pages, isEdit]);

  const dynamicLinkTitleMap = useMemo(() => {
    if (!Array.isArray(recordData?.dynamic_link_uuids)) return {};
    const map = {};
    recordData.dynamic_link_uuids.forEach((d) => {
      const id = typeof d === "string" ? d : d.dynamic_link_id;
      if (id) map[id] = typeof d === "string" ? id : d.title || id;
    });
    return map;
  }, [recordData?.dynamic_link_uuids]);

  const dynamicPagesOptions = useMemo(() => {
    if (!isEdit || !initialValues[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS]?.length)
      return allDynamicPagesData;
    const options = [...(allDynamicPagesData || [])];
    initialValues[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS].forEach((id) => {
      if (
        !options.find(
          (o) => o.dynamic_link_id === id || o.uuid === id || o.value === id
        )
      ) {
        const title = dynamicLinkTitleMap[id] || id;
        options.push({
          dynamic_link_id: id,
          uuid: id,
          id,
          value: id,
          label: title,
          title,
        });
      }
    });
    return options;
  }, [allDynamicPagesData, isEdit, initialValues, dynamicLinkTitleMap]);

  const offeringOptions = useMemo(
    () => getEditOfferingFallback(infiniteListingOptions),
    [infiniteListingOptions, getEditOfferingFallback]
  );

  return {
    comboPagesOptions,
    recordData,
    handleCreateOffer,
    isEdit,
    initialValues,
    DateField,
    firstLimitedOffer,
    limitedOfferCreative,
    listingUuid,
    selectedListing,
    shareUrl,
    getEditOfferingFallback,
    shouldShowOfferingSelector,
    shouldShowDynamicLinkSelector,
    shouldShowPriceFields,
    shouldShowInternationalPricing,
    dynamicLinkTitleMap,
    dynamicPagesOptions,
    offeringOptions,
  };
};

export { useLimitedTimeOffer };
